import React, {useRef} from "react"
import {useClickAway, useToggle} from "react-use"
import {concat, without} from "lodash"
import classNames from "classnames"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faAngleDown} from "@fortawesome/free-solid-svg-icons"

export type DomainOption = {
	key: string
	label: string
	color: string
}

export const domainOptions: DomainOption[] = [
	{key: "dashboard", label: "Dashboard", color: "#2e75ff"},
	{key: "rtl-bill-pay", label: "RTL Bill Pay", color: "#e562f0"},
	{key: "rtl-banking", label: "RTL Banking", color: "#18f3bf"},
	{key: "rtl-capital", label: "RTL Capital", color: "#ff506f"},
	{key: "fraud-disputes", label: "Fraud & Disputes", color: "#936dff"},
	{key: "application-form", label: "Application Form", color: "#ff9d42"},
	{key: "uncategorized", label: "Uncategorized", color: "#30e87a"},
]

export function DomainFilter({
	statuses,
	setStatuses,
	title = "",
	onFilterFunc,
	singleOption = false,
}: {
	statuses: string[]
	setStatuses: (statuses: string[]) => void
	title?: string
	onFilterFunc?: () => void
	singleOption?: boolean
}) {
	const ref = useRef(null)
	const [isDropdownActive, toggleDropdown] = useToggle(false)
	const showFilterSummary = statuses.length > 0 && statuses.length !== domainOptions.length

	useClickAway(ref, () => toggleDropdown(false))
	const dropdownClassname = classNames("dropdown", isDropdownActive && "is-active")

	function toggle(status: string) {
		if (singleOption) {
			setStatuses([status])
			return
		}

		if (statuses.includes(status)) {
			setStatuses(without(statuses, status))
			return
		}

		if (!statuses.includes(status)) {
			setStatuses(concat(statuses, status))
			return
		}
	}

	const renderButtonRow = (option: DomainOption) => {
		return (
			<label key={option.key} className="panel-block no-border-bottom wide-panel simple-checkbox domain-filter-option">
				<input
					type="checkbox"
					onChange={(_) => {
						toggle(option.label)
						onFilterFunc ? onFilterFunc() : null
					}}
					checked={statuses.includes(option.label)}
				/>
				<div className="domain-option-content">
					<div className="domain-color-dot" style={{backgroundColor: option.color}} />
					<span className="domain-option-label">{option.label}</span>
				</div>
			</label>
		)
	}

	const buttons = domainOptions.map((option) => renderButtonRow(option))

	return (
		<div className={dropdownClassname} ref={ref}>
			<div className="dropdown-trigger">
				<button
					className="button is-ghost"
					aria-haspopup="true"
					aria-controls="dropdown-menu"
					onClick={() => toggleDropdown()}
				>
					<div className="filter-title-placeholder">
						{showFilterSummary && <div className="filter-title-count" />}
						<span>
							{title} {showFilterSummary && `(${statuses.length})`}
						</span>
					</div>
					<span className="icon is-small">
						<FontAwesomeIcon icon={faAngleDown} />
					</span>
				</button>
			</div>
			<div className="dropdown-menu" id="dropdown-menu" role="menu">
				<div className="dropdown-content">
					<div className="panel is-shadowless is-marginless limeted-height">{buttons}</div>
				</div>
			</div>
		</div>
	)
}
